
import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { WeeklySummary, MeetingNote } from '../types';
import { sendEmailNotification } from '../services/gmailService';
import { generateAudioFromText } from '../services/geminiService';
import MindMap from './MindMap';
import Infographic from './Infographic';
import ChatBroadcastModal from './ChatBroadcastModal';

interface Props {
  summary: WeeklySummary;
  notes: MeetingNote[];
  accessToken: string | null;
  userEmail: string | null;
}

const VIEW_OPTIONS = [
  { id: 'concise', label: 'Concise', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /> },
  { id: 'detailed', label: 'Detailed', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /> },
  { id: 'key-outcomes', label: 'Outcomes', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /> },
  { id: 'executive-qa', label: 'Q&A', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /> },
  { id: 'action-board', label: 'Actions', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /> },
  { id: 'timeline', label: 'Timeline', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /> },
  { id: 'risks-blockers', label: 'Risks', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /> },
  { id: 'mindmap', label: 'Mind Map', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /> },
  { id: 'infographic', label: 'Infographic', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /> },
] as const;

const Dashboard: React.FC<Props> = ({ summary, accessToken, userEmail }) => {
  const [viewType, setViewType] = useState<'concise' | 'detailed' | 'mindmap' | 'infographic' | 'action-board' | 'risks-blockers' | 'executive-qa' | 'timeline' | 'key-outcomes'>('detailed');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [isEmailPromptOpen, setIsEmailPromptOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioData, setAudioData] = useState<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);

  const activeContent = viewType === 'concise' ? summary.conciseOverview : summary.detailedOverview;

  const playPcmAudio = (base64Data: string) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      }
      const ctx = audioContextRef.current;
      
      const binaryString = window.atob(base64Data);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const int16Array = new Int16Array(bytes.buffer);
      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768.0;
      }
      
      const audioBuffer = ctx.createBuffer(1, float32Array.length, 24000);
      audioBuffer.getChannelData(0).set(float32Array);
      
      if (sourceNodeRef.current) {
        sourceNodeRef.current.stop();
      }
      
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      source.onended = () => setIsPlaying(false);
      source.start();
      sourceNodeRef.current = source;
      setIsPlaying(true);
    } catch (error) {
      console.error("Error playing audio:", error);
      setSendError("Failed to play audio format.");
    }
  };

  const handlePlayAudio = async () => {
    if (isPlaying) {
      sourceNodeRef.current?.stop();
      setIsPlaying(false);
      return;
    }

    if (audioData) {
      playPcmAudio(audioData);
      return;
    }
    
    setIsGeneratingAudio(true);
    try {
      const base64Data = await generateAudioFromText(summary.detailedOverview);
      setAudioData(base64Data);
      playPcmAudio(base64Data);
    } catch (err: any) {
      setSendError(err.message || "Failed to generate audio");
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  const handleSendEmail = async (targetEmail: string) => {
    if (!accessToken || !targetEmail) return;
    
    setIsSending(true);
    setSendError(null);
    setSendSuccess(false);
    setIsEmailPromptOpen(false);
    
    try {
      await sendEmailNotification(accessToken, targetEmail, summary);
      setSendSuccess(true);
      setTimeout(() => setSendSuccess(false), 5000);
    } catch (err: any) {
      setSendError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">
      {/* Overview Section */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        <div className="flex flex-col gap-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full">
            <div className="flex items-center space-x-3">
              <div className="bg-indigo-100 p-2.5 rounded-xl">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Executive Intelligence</h2>
            </div>

            {accessToken && (
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => {
                    if (!userEmail) {
                      setIsEmailPromptOpen(true);
                    } else {
                      handleSendEmail(userEmail);
                    }
                  }}
                  disabled={isSending}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all border ${
                    sendSuccess 
                      ? 'bg-emerald-50 text-emerald-600 border-emerald-200' 
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                  } disabled:opacity-50`}
                >
                  {isSending ? (
                    <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  ) : sendSuccess ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  )}
                  <span>{sendSuccess ? 'Sent to Inbox' : 'Send to Email'}</span>
                </button>
                <button
                  onClick={() => setIsChatModalOpen(true)}
                  className="flex items-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all border bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>Send to Chat</span>
                </button>
              </div>
            )}
          </div>

          <div className="w-full mb-2">
            <div className="flex flex-wrap gap-1.5">
              {VIEW_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setViewType(option.id as any)}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-all border ${
                    viewType === option.id 
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' 
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {option.icon}
                  </svg>
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {sendError && (
          <div className="mb-6 p-3 bg-red-50 border border-red-100 text-red-600 text-xs rounded-xl font-medium">
            {sendError}
          </div>
        )}

        {viewType === 'mindmap' ? (
          <div className="mt-2 animate-in fade-in slide-in-from-top-4 duration-500">
            <MindMap data={summary.mindMap} />
          </div>
        ) : viewType === 'infographic' ? (
          <div className="mt-2 animate-in fade-in slide-in-from-top-4 duration-500">
            <Infographic data={summary.infographic} />
          </div>
        ) : viewType === 'key-outcomes' ? (
          <div className="mt-2 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 flex flex-col">
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-emerald-100 p-2.5 rounded-xl">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 tracking-tight">Key Outcomes</h3>
              </div>
              <ul className="space-y-4 flex-grow">
                {summary.keyOutcomes.map((outcome, i) => (
                  <li key={i} className="flex items-start bg-slate-50 p-4 rounded-xl border border-slate-100 transition-colors hover:bg-emerald-50/30">
                    <span className="text-emerald-500 mr-4 mt-1 flex-shrink-0">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span className="text-slate-700 text-lg font-semibold leading-relaxed">{outcome}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : viewType === 'action-board' ? (
          <div className="mt-2 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {summary.tasksPerPerson.map((person, i) => (
                <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-5">
                    <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-lg mr-4 shadow-sm">
                      {person.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex flex-col">
                      <h3 className="font-bold text-slate-900 text-lg tracking-tight">
                        {person.name.split('<')[0].trim()}
                      </h3>
                      {person.name.includes('<') && (
                        <span className="text-[10px] text-slate-500 font-normal mt-0.5">
                          &lt;{person.name.split('<')[1].replace('>', '')}&gt;
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-3">
                    {person.tasks.map((task, j) => (
                      <div key={j} className="group flex items-start justify-between space-x-3 bg-white p-3.5 rounded-xl border border-slate-100 shadow-sm hover:border-indigo-200 transition-colors">
                        <div className="flex items-start space-x-3">
                          <div className="mt-0.5 flex-shrink-0">
                            <svg className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <rect x="3" y="3" width="18" height="18" rx="5" strokeWidth="2" />
                            </svg>
                          </div>
                          <span className="font-medium text-sm text-slate-700 leading-snug">{task}</span>
                        </div>
                        <a 
                          href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(task)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-indigo-500 hover:bg-indigo-50 rounded-lg flex-shrink-0"
                          title="Add to Calendar"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </a>
                      </div>
                    ))}
                    {person.tasks.length === 0 && (
                      <div className="text-sm text-slate-400 italic text-center py-4">No tasks assigned</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : viewType === 'risks-blockers' ? (
          <div className="mt-2 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="bg-rose-50/50 rounded-2xl p-8 border border-rose-100">
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-rose-100 p-2.5 rounded-xl">
                  <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-rose-900 tracking-tight">Critical Risks & Blockers</h3>
              </div>
              
              {summary.risksAndBlockers && summary.risksAndBlockers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {summary.risksAndBlockers.map((risk, i) => (
                    <div key={i} className="flex items-start bg-white p-5 rounded-xl border border-rose-200 shadow-sm hover:shadow-md transition-shadow">
                      <span className="text-rose-500 mr-4 mt-0.5 flex-shrink-0">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                      <span className="text-slate-800 font-medium leading-relaxed">{risk}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white p-8 rounded-xl border border-rose-100 text-center">
                  <svg className="w-12 h-12 text-emerald-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-slate-600 font-medium text-lg">No critical risks or blockers identified in the current workspace items.</p>
                </div>
              )}
            </div>
          </div>
        ) : viewType === 'executive-qa' ? (
          <div className="mt-2 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <div className="flex items-center space-x-3 mb-8">
                <div className="bg-indigo-100 p-2.5 rounded-xl">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 tracking-tight">Executive Briefing Q&A</h3>
              </div>
              
              {summary.executiveQA && summary.executiveQA.length > 0 ? (
                <div className="space-y-6">
                  {summary.executiveQA.map((qa, i) => (
                    <div key={i} className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
                      <h4 className="text-lg font-bold text-slate-900 mb-3 flex items-start">
                        <span className="text-indigo-500 mr-3 mt-0.5">Q:</span>
                        {qa.question}
                      </h4>
                      <div className="text-slate-600 leading-relaxed flex items-start">
                        <span className="text-slate-400 font-bold mr-3 mt-0.5">A:</span>
                        <p>{qa.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white p-8 rounded-xl border border-slate-100 text-center">
                  <p className="text-slate-500 font-medium">No Q&A generated for this workspace.</p>
                </div>
              )}
            </div>
          </div>
        ) : viewType === 'timeline' ? (
          <div className="mt-2 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
              <div className="flex items-center space-x-3 mb-8">
                <div className="bg-indigo-100 p-2.5 rounded-xl">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 tracking-tight">Project Timeline</h3>
              </div>
              
              {summary.timelineEvents && summary.timelineEvents.length > 0 ? (
                <div className="relative border-l-2 border-indigo-100 ml-4 space-y-8 pb-4">
                  {summary.timelineEvents.map((event, i) => (
                    <div key={i} className="relative pl-8">
                      <div className={`absolute -left-2.5 top-1.5 w-5 h-5 rounded-full border-4 border-white shadow-sm ${
                        event.type === 'deadline' ? 'bg-rose-500' :
                        event.type === 'meeting' ? 'bg-blue-500' :
                        event.type === 'decision' ? 'bg-amber-500' :
                        'bg-emerald-500'
                      }`}></div>
                      <div className="bg-slate-50 p-5 rounded-xl border border-slate-100 hover:border-indigo-200 transition-colors">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <span className="text-sm font-bold text-indigo-600 uppercase tracking-wider">{event.date}</span>
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
                            event.type === 'deadline' ? 'bg-rose-100 text-rose-700' :
                            event.type === 'meeting' ? 'bg-blue-100 text-blue-700' :
                            event.type === 'decision' ? 'bg-amber-100 text-amber-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {event.type}
                          </span>
                        </div>
                        <h4 className="text-lg font-bold text-slate-900 mb-2">{event.title}</h4>
                        <p className="text-slate-600 leading-relaxed">{event.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-50 p-8 rounded-xl border border-slate-100 text-center">
                  <p className="text-slate-500 font-medium">No timeline events identified in the current workspace items.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {viewType === 'detailed' && (
              <div className="mb-4 flex items-center bg-indigo-50/50 p-3 rounded-xl border border-indigo-100 w-fit">
                <button 
                  onClick={handlePlayAudio}
                  disabled={isGeneratingAudio}
                  className="flex items-center space-x-2 text-indigo-700 hover:text-indigo-800 font-medium text-sm px-3 py-1.5 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50"
                >
                  {isGeneratingAudio ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-indigo-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating Audio...
                    </>
                  ) : isPlaying ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Stop Audio</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Listen to Detailed Summary</span>
                    </>
                  )}
                </button>
              </div>
            )}
            <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed text-lg font-medium transition-all duration-300">
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p className="mb-4 animate-in fade-in slide-in-from-top-1 duration-500">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc pl-5 mb-4 space-y-2">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal pl-5 mb-4 space-y-2">{children}</ol>,
                  li: ({ children }) => <li className="text-slate-700">{children}</li>,
                  h3: ({ children }) => <h3 className="text-xl font-bold text-slate-900 mt-6 mb-2">{children}</h3>,
                  strong: ({ children }) => <strong className="text-indigo-700 font-bold">{children}</strong>,
                }}
              >
                {activeContent}
              </ReactMarkdown>
            </div>
            
            <div className="mt-8 pt-8 border-t border-slate-100 flex flex-wrap gap-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mr-2 py-1">Core Pillars:</span>
              {summary.topThemes.map((theme, i) => (
                <span key={i} className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-full border border-indigo-100 uppercase tracking-tighter">
                  {theme}
                </span>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Grounding Sources */}
      {summary.groundingChunks && summary.groundingChunks.length > 0 && (
        <section className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
          <div className="flex items-center space-x-2 mb-6">
            <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            <h3 className="text-xl font-bold tracking-tight text-slate-900">Research Grounding</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {summary.groundingChunks.map((chunk, i) => chunk.web && chunk.web.uri && (
              <a 
                key={i} 
                href={chunk.web.uri} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white hover:bg-slate-50 p-4 rounded-xl border border-slate-200 transition-all flex flex-col group shadow-sm"
              >
                <span className="text-xs font-bold text-indigo-600 mb-1 uppercase tracking-widest">Verified Context</span>
                <span className="text-sm font-semibold text-slate-800 group-hover:underline line-clamp-1">{chunk.web.title || 'Referenced Source'}</span>
                <span className="text-[10px] text-slate-400 mt-2 truncate">{chunk.web.uri}</span>
              </a>
            ))}
          </div>
        </section>
      )}

      <footer className="pt-8 text-center border-t border-slate-200">
        <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">
          Consolidated Week-at-a-glance Intelligence • Gemini Flash 3.0
        </p>
      </footer>

      {accessToken && (
        <ChatBroadcastModal 
          isOpen={isChatModalOpen}
          onClose={() => setIsChatModalOpen(false)}
          accessToken={accessToken}
          summary={summary}
        />
      )}

      {isEmailPromptOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-slate-800 flex items-center">
                <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send to Email
              </h2>
              <button onClick={() => setIsEmailPromptOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-bold text-slate-700">Email Address</label>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="name@company.com"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-sm font-medium text-slate-700"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && emailInput) {
                      handleSendEmail(emailInput);
                    }
                  }}
                />
                <p className="text-xs text-slate-500">We couldn't automatically detect your email address. Please enter where you'd like to receive the report.</p>
              </div>
              <button
                onClick={() => handleSendEmail(emailInput)}
                disabled={!emailInput || isSending}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-xl font-bold shadow-md transition-all flex items-center justify-center space-x-2"
              >
                {isSending ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <span>Send Report</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
